import { useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";
import type { WidgetMessage } from "@/types";
import { useSession } from "@/store/session-store";
import { playNotification } from "@/lib/sound";

/**
 * Connects to the public Socket.IO namespace `/webchat` as a visitor and
 * dispatches real-time events to the Zustand store. Lives as long as
 * the widget tab is open.
 *
 * Returned ref exposes the socket so components can emit ad-hoc events
 * (typing, ping for presence, message:read acks).
 */
export function useWebchatSocket(): {
  socket: Socket | null;
} {
  const socketRef = useRef<Socket | null>(null);
  const apiUrl = useSession((s) => s.apiUrl);
  const sessionToken = useSession((s) => s.sessionToken);
  const addMessage = useSession((s) => s.addMessage);
  const updateMessageStatus = useSession((s) => s.updateMessageStatus);
  const setSocketConnected = useSession((s) => s.setSocketConnected);
  const setAgentTyping = useSession((s) => s.setAgentTyping);

  useEffect(() => {
    if (!apiUrl || !sessionToken) return;

    const socket = io(`${apiUrl}/webchat`, {
      auth: { token: sessionToken },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    socketRef.current = socket;

    const onConnect = () => setSocketConnected(true);
    const onDisconnect = () => setSocketConnected(false);

    const onMessageNew = (data: { message?: WidgetMessage } & WidgetMessage) => {
      // Server shapes the payload as either `{ message: {...} }` (dashboard
      // broadcast) or the plain message (webchat provider emit). Accept both.
      const msg = (data.message ?? data) as WidgetMessage;
      if (!msg?.id) return;
      addMessage(msg);
      if (
        msg.direction === "outbound" &&
        useSession.getState().branding?.soundEnabled !== false
      ) {
        playNotification();
      }
    };

    const onMessageStatus = (data: {
      messageId: string;
      status: WidgetMessage["status"];
    }) => {
      updateMessageStatus(data.messageId, data.status);
    };

    const onTyping = (data: { isTyping?: boolean; origin?: string }) => {
      // `origin === "visitor"` is OUR own typing echo — ignore.
      if (data.origin === "visitor") return;
      setAgentTyping(!!data.isTyping);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("message:new", onMessageNew);
    socket.on("message:status", onMessageStatus);
    socket.on("typing", onTyping);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("message:new", onMessageNew);
      socket.off("message:status", onMessageStatus);
      socket.off("typing", onTyping);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [
    apiUrl,
    sessionToken,
    addMessage,
    updateMessageStatus,
    setSocketConnected,
    setAgentTyping,
  ]);

  return { socket: socketRef.current };
}
